import React from 'react';
import { useParams } from 'react-router-dom';
import { FONT_BODY, FONT_HEADING, themeColors } from '../theme/colors';
import { renderMarkdownToHtml } from './chat/markdownProcessing';
import {
  ShareAccess,
  ShareLayout,
  SharedMessage,
  getShareMessages,
  getShareMeta,
  requestShareOtp,
  unlockShare,
  verifyShareOtp,
} from './chat/shareService';

const dark = themeColors.dark;
const paper = {
  bg: '#F4F1EA',
  ink: '#1C1916',
  ink2: '#5C564E',
  ink3: '#7A746A',
  line: 'rgba(28,25,22,0.10)',
  line2: 'rgba(28,25,22,0.16)',
  card: '#FFFCF7',
  jade: '#0B7E57',
};

const sourceUrl = (item: any): string | null => {
  if (!item) return null;
  if (typeof item === 'string') return item;
  if (typeof item.url === 'string') return item.url;
  return null;
};

const sourceLabel = (item: any, index: number): string => {
  if (item && typeof item === 'object' && item.title) return String(item.title);
  const url = sourceUrl(item);
  if (!url) return `Sumber ${index + 1}`;
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const ShareView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [phase, setPhase] = React.useState<'loading' | 'gate' | 'ready' | 'missing'>('loading');
  const [access, setAccess] = React.useState<ShareAccess>('public');
  const [layout, setLayout] = React.useState<ShareLayout>('transcript');
  const [title, setTitle] = React.useState<string | null>(null);
  const [brief, setBrief] = React.useState<string | null>(null);
  const [allowTranscript, setAllowTranscript] = React.useState(false);
  const [messages, setMessages] = React.useState<SharedMessage[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);
  const [password, setPassword] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [code, setCode] = React.useState('');
  const [otpSent, setOtpSent] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [showTranscript, setShowTranscript] = React.useState(false);

  const loadMessages = React.useCallback(async (shareToken: string, nextLayout?: ShareLayout) => {
    const data = await getShareMessages(shareToken);
    setTitle(data.title);
    setMessages(data.messages || []);
    setBrief(data.brief || null);
    setAllowTranscript(!!data.show_transcript && (data.messages || []).length > 0);
    setLayout(data.layout || nextLayout || 'transcript');
    setPhase('ready');
  }, []);

  React.useEffect(() => {
    if (!token) {
      setPhase('missing');
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const meta = await getShareMeta(token);
        if (cancelled) return;
        setAccess(meta.access);
        if (meta.layout) setLayout(meta.layout);
        if (meta.unlocked) {
          await loadMessages(token, meta.layout);
        } else {
          setPhase('gate');
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.message || 'Tautan ini sudah tidak berlaku.');
          setPhase('missing');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token, loadMessages]);

  React.useEffect(() => {
    const label = title ? `${title} · Power AI` : 'Percakapan dibagikan · Power AI';
    document.title = label;
    const description = (() => {
      if (!brief) return null;
      const plain = brief.replace(/[#*_`>\-\[\]]+/g, ' ').replace(/\s+/g, ' ').trim();
      if (!plain) return null;
      return plain.length > 180 ? `${plain.slice(0, 177).trim()}...` : plain;
    })();
    const setMeta = (attr: string, key: string, value: string) => {
      let node = document.querySelector(`meta[${attr}="${key}"]`);
      if (!node) {
        node = document.createElement('meta');
        node.setAttribute(attr, key);
        document.head.appendChild(node);
      }
      node.setAttribute('content', value);
    };
    setMeta('name', 'robots', 'noindex, nofollow');
    if (title) {
      setMeta('property', 'og:title', title);
      setMeta('name', 'twitter:title', title);
    }
    if (description) {
      setMeta('name', 'description', description);
      setMeta('property', 'og:description', description);
      setMeta('name', 'twitter:description', description);
    }
    if (token) {
      setMeta('property', 'og:url', `${window.location.origin}/s/${token}`);
    }
  }, [title, brief, token]);

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await unlockShare(token, password);
      await loadMessages(token);
    } catch (err: any) {
      setError(err?.message || 'Kata sandi salah.');
    } finally {
      setBusy(false);
    }
  };

  const submitEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const message = await requestShareOtp(token, email);
      setOtpSent(true);
      setNotice(message);
    } catch (err: any) {
      setError(err?.message || 'Gagal mengirim kode.');
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setBusy(true);
    setError(null);
    try {
      await verifyShareOtp(token, email, code);
      await loadMessages(token);
    } catch (err: any) {
      setError(err?.message || 'Kode salah atau kedaluwarsa.');
    } finally {
      setBusy(false);
    }
  };

  const isBrief = phase === 'ready' && layout === 'brief' && !!brief;
  const c = isBrief ? {
    ...dark,
    appBackground: paper.bg,
    ink: paper.ink,
    ink2: paper.ink2,
    ink3: paper.ink3,
    line: paper.line,
    line2: paper.line2,
    panel: paper.card,
    btnPrimaryBg: paper.ink,
    btnPrimaryText: '#FFFCF7',
    jade: paper.jade,
  } : dark;

  return (
    <div style={{
      height: '100%',
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch',
      background: isBrief ? paper.bg : dark.appBackground,
      color: isBrief ? paper.ink : dark.ink,
      fontFamily: FONT_BODY,
    }}>
      <header style={{
        height: '52px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 20px',
        borderBottom: `1px solid ${c.line}`,
        position: 'sticky',
        top: 0,
        zIndex: 2,
        background: isBrief ? paper.card : '#0A0B0D',
      }}>
        <a
          href="https://powerai.id"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            color: c.ink, textDecoration: 'none', fontWeight: 600, fontSize: '13.5px',
            fontFamily: FONT_HEADING,
          }}
        >
          <img src="/logo-only200-removebg.png" alt="" width={20} height={20} />
          Power AI
        </a>
        <span style={{ fontSize: '11.5px', color: c.ink3 }}>
          {isBrief ? 'Ringkasan' : 'Dibagikan lewat Power AI'}
        </span>
      </header>

      <main style={{
        maxWidth: isBrief ? '740px' : '720px',
        margin: '0 auto',
        padding: isBrief ? '36px 24px 80px' : '28px 20px 64px',
      }}>
        {phase === 'loading' && (
          <div style={{ fontSize: '13px', color: c.ink3 }}>Membuka percakapan…</div>
        )}

        {phase === 'missing' && (
          <div>
            <h1 style={{ fontSize: '20px', fontWeight: 650, margin: '0 0 8px' }}>
              Tautan tidak berlaku
            </h1>
            <p style={{ fontSize: '13.5px', color: c.ink2, margin: '0 0 16px' }}>
              {error || 'Tautan ini sudah tidak berlaku.'}
            </p>
            <a
              href={localStorage.getItem('accessToken') ? '/c' : 'https://powerai.id'}
              style={{
                display: 'inline-flex', alignItems: 'center', height: '34px',
                padding: '0 14px', borderRadius: '99px',
                background: dark.btnPrimaryBg, color: dark.btnPrimaryText,
                fontSize: '13px', fontWeight: 600, textDecoration: 'none',
              }}
            >
              Ke Power AI
            </a>
          </div>
        )}

        {phase === 'gate' && (
          <div style={{
            maxWidth: '400px',
            padding: '18px',
            borderRadius: '14px',
            border: `1px solid ${dark.line2}`,
            background: dark.panel,
            color: dark.ink,
          }}>
            <h1 style={{ fontSize: '18px', fontWeight: 650, margin: '0 0 6px' }}>
              Percakapan dibagikan
            </h1>
            <p style={{ fontSize: '13px', color: dark.ink3, margin: '0 0 16px' }}>
              {access === 'password'
                ? 'Isi kata sandi untuk membacanya.'
                : 'Isi email yang diizinkan. Kami kirim kode OTP.'}
            </p>

            {access === 'password' && (
              <form onSubmit={submitPassword}>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Kata sandi"
                  autoFocus
                  style={inputStyle(dark)}
                />
                <button type="submit" disabled={busy || !password} style={buttonStyle(dark, busy)}>
                  Buka
                </button>
              </form>
            )}

            {access === 'email' && (
              <>
                <form onSubmit={submitEmail}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="email@perusahaan.com"
                    autoFocus
                    style={inputStyle(dark)}
                  />
                  <button type="submit" disabled={busy || !email} style={buttonStyle(dark, busy)}>
                    Kirim kode
                  </button>
                </form>
                {otpSent && (
                  <form onSubmit={submitCode} style={{ marginTop: '12px' }}>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="Kode 6 digit"
                      style={inputStyle(dark)}
                    />
                    <button type="submit" disabled={busy || code.length !== 6} style={buttonStyle(dark, busy)}>
                      Verifikasi
                    </button>
                  </form>
                )}
              </>
            )}

            {notice && (
              <div style={{ marginTop: '12px', fontSize: '12.5px', color: dark.ink2 }}>{notice}</div>
            )}
            {error && (
              <div style={{ marginTop: '12px', fontSize: '12.5px', color: '#F87171' }}>{error}</div>
            )}
          </div>
        )}

        {phase === 'ready' && isBrief && (
          <article>
            <div style={{ fontSize: '11.5px', fontWeight: 600, color: paper.ink3, letterSpacing: '0.08em', marginBottom: '10px' }}>
              RINGKASAN
            </div>
            <h1 style={{
              fontSize: '28px', fontWeight: 650, letterSpacing: '-0.03em',
              margin: '0 0 10px', lineHeight: 1.25, color: paper.ink,
            }}>
              {title || 'Ringkasan'}
            </h1>
            <p style={{ fontSize: '13px', color: paper.ink3, margin: '0 0 28px' }}>
              Disusun dari percakapan Power AI. Hanya baca.
            </p>
            <div
              className="pa-body"
              style={{
                fontSize: '16.5px',
                lineHeight: 1.7,
                color: paper.ink,
              }}
              dangerouslySetInnerHTML={{ __html: renderMarkdownToHtml(brief || '', 'light') }}
            />
            <div style={{ marginTop: '36px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={() => window.print()}
                style={{
                  height: '32px', padding: '0 12px', borderRadius: '99px',
                  border: `1px solid ${paper.line2}`, background: 'transparent',
                  color: paper.ink2, fontSize: '12.5px', cursor: 'pointer',
                }}
              >
                Cetak
              </button>
              {allowTranscript && (
                <button
                  type="button"
                  onClick={() => setShowTranscript(!showTranscript)}
                  style={{
                    height: '32px', padding: '0 12px', borderRadius: '99px',
                    border: `1px solid ${paper.line2}`, background: 'transparent',
                    color: paper.ink2, fontSize: '12.5px', cursor: 'pointer',
                  }}
                >
                  {showTranscript ? 'Sembunyikan percakapan' : 'Lihat percakapan lengkap'}
                </button>
              )}
            </div>
            {showTranscript && (
              <div style={{ marginTop: '28px' }}>
                <Transcript messages={messages} theme="light" />
              </div>
            )}
          </article>
        )}

        {phase === 'ready' && !isBrief && (
          <>
            <h1 style={{
              fontSize: '22px', fontWeight: 650, letterSpacing: '-0.02em',
              margin: '0 0 6px', lineHeight: 1.3,
            }}>
              {title || 'Percakapan dibagikan'}
            </h1>
            <div style={{ fontSize: '12px', color: dark.ink3, marginBottom: '22px' }}>Hanya baca</div>
            <Transcript messages={messages} theme="dark" />
          </>
        )}
      </main>
    </div>
  );
};

const Transcript: React.FC<{ messages: SharedMessage[]; theme: 'light' | 'dark' }> = ({ messages, theme }) => {
  const ink = theme === 'light' ? paper.ink : dark.ink;
  const ink3 = theme === 'light' ? paper.ink3 : dark.ink3;
  const userBg = theme === 'light' ? 'rgba(28,25,22,0.06)' : dark.messageBackground.user;
  const line2 = theme === 'light' ? paper.line2 : dark.line2;
  if (messages.length === 0) {
    return <div style={{ fontSize: '13.5px', color: ink3 }}>Belum ada pesan.</div>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
      {messages.map((message) => {
        const isUser = message.role === 'user';
        const sources = Array.isArray(message.sources) ? message.sources : [];
        return (
          <article key={message.id}>
            <div style={{
              fontSize: '11px', fontWeight: 600, color: ink3,
              marginBottom: '6px', letterSpacing: '0.02em',
            }}>
              {isUser ? 'Pengirim' : 'Power AI'}
            </div>
            <div
              className="pa-body"
              style={{
                fontSize: '15px',
                lineHeight: 1.65,
                color: ink,
                padding: isUser ? '10px 12px' : 0,
                borderRadius: isUser ? '12px' : 0,
                background: isUser ? userBg : 'transparent',
                maxWidth: isUser ? '92%' : '100%',
              }}
              dangerouslySetInnerHTML={{
                __html: renderMarkdownToHtml(message.content || '', theme),
              }}
            />
            {sources.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px' }}>
                {sources.map((item, index) => {
                  const href = sourceUrl(item);
                  const label = sourceLabel(item, index);
                  const chip = {
                    fontSize: '11px',
                    color: ink3,
                    border: `1px solid ${line2}`,
                    borderRadius: '99px',
                    padding: '2px 8px',
                    textDecoration: 'none',
                  } as React.CSSProperties;
                  return href ? (
                    <a key={`${message.id}-${index}`} href={href} target="_blank" rel="noopener noreferrer" style={chip}>
                      {label}
                    </a>
                  ) : (
                    <span key={`${message.id}-${index}`} style={chip}>{label}</span>
                  );
                })}
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
};

const inputStyle = (colors: any): React.CSSProperties => ({
  width: '100%',
  padding: '10px 12px',
  borderRadius: '8px',
  border: `1px solid ${colors.line2}`,
  background: 'rgba(255,255,255,0.04)',
  color: colors.ink,
  fontSize: '14px',
  fontFamily: 'inherit',
  marginBottom: '10px',
});

const buttonStyle = (colors: any, busy: boolean): React.CSSProperties => ({
  height: '34px',
  padding: '0 14px',
  borderRadius: '99px',
  border: 'none',
  background: colors.btnPrimaryBg,
  color: colors.btnPrimaryText,
  fontSize: '13px',
  fontWeight: 600,
  cursor: busy ? 'progress' : 'pointer',
});

export default ShareView;
